import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import { getUser } from './api/auth';
import type { User } from './types/auth';

type View = 'dashboard' | 'admin' | 'fire';

function App() {
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<View>('dashboard');

  // Check for session token on mount (from URL or localStorage)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionFromUrl = urlParams.get('session');

    if (sessionFromUrl) {
      // Store token and clean URL
      localStorage.setItem('sessionToken', sessionFromUrl);
      setSessionToken(sessionFromUrl);
      window.history.replaceState({}, '', '/');
    } else {
      // Check localStorage
      const storedToken = localStorage.getItem('sessionToken');
      if (storedToken) {
        setSessionToken(storedToken);
      }
    }
  }, []);

  // Fetch user data if we have a session token
  const { data: user, isLoading, error } = useQuery<User>({
    queryKey: ['user', sessionToken],
    queryFn: getUser,
    enabled: !!sessionToken,
    retry: false,
  });

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('sessionToken');
    setSessionToken(null);
    setCurrentView('dashboard');
  };

  // If no session token, show login
  if (!sessionToken) {
    return <Login />;
  }

  // If loading user data, show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If error or no user, clear session and show login
  if (error || !user) {
    localStorage.removeItem('sessionToken');
    return <Login />;
  }

  // Render the appropriate view
  if (currentView === 'admin' && user.is_admin) {
    return <AdminDashboard onBack={() => setCurrentView('dashboard')} />;
  }

  return (
    <Dashboard 
      user={user} 
      onLogout={handleLogout}
      onAdminClick={user.is_admin ? () => setCurrentView('admin') : undefined}
      onFIREClick={() => setCurrentView('fire')}
      currentView={currentView}
      onViewChange={setCurrentView}
    />
  );
}

export default App;
